/**
 * Purchase Orders List Page
 * BuilderBel Construction Project Management System
 */
import {
  Check,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  Download,
  Eye,
  FileText,
  Inbox,
  Pencil,
  Plus,
  Send,
  ShoppingCart,
  Trash2,
  XCircle,
} from 'lucide-react';
import React, { useCallback, useMemo, useState } from 'react';
import { SearchableDropdown } from '../components/forms';
import LoadingSpinner from '../components/LoadingSpinner';
import {
  ChangeOrderDrawer,
  PurchaseOrderDrawer,
  ReceiptDrawer,
  SendPurchaseOrderModal,
} from '../components/purchaseOrders';
import {
  ActionMenu,
  type ActionMenuItem,
  EmptyState,
  ModuleLayout,
  PriorityBadge,
  StatusBadge,
} from '../components/ui';
import { useToast } from '../contexts/ToastContext';
import { useCurrentProjectId } from '../hooks/useProjectRedux';
import {
  useDeletePurchaseOrderMutation,
  useGetPurchaseOrdersQuery,
  useGetPurchaseOrderStatsQuery,
  useProcessPurchaseOrderApprovalMutation,
  useUpdatePurchaseOrderMutation,
} from '../services/purchaseOrderApi';
import type {
  PurchaseOrder,
  PurchaseOrderPriority,
  PurchaseOrderStatus,
  PurchaseOrderType,
} from '../types/purchaseOrder';

// Status filter options - matches backend enum
const STATUS_OPTIONS = [
  { value: '', label: 'All Status' },
  { value: 'draft', label: 'Draft' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'sent', label: 'Sent' },
  { value: 'partial', label: 'Partial' },
  { value: 'complete', label: 'Complete' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'void', label: 'Void' },
];

// Type filter options - matches backend enum
const TYPE_OPTIONS = [
  { value: '', label: 'All Types' },
  { value: 'material', label: 'Material' },
  { value: 'equipment', label: 'Equipment' },
  { value: 'subcontract', label: 'Subcontract' },
  { value: 'service', label: 'Service' },
];

// Priority filter options (for future use when backend supports priority filtering)
const _PRIORITY_OPTIONS = [
  { value: '', label: 'All Priorities' },
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];

// Sort options - matches backend listQuerySchema
const SORT_OPTIONS = [
  { value: 'createdAt', label: 'Created Date' },
  { value: 'issueDate', label: 'Issue Date' },
  { value: 'dueDate', label: 'Due Date' },
  { value: 'total', label: 'Amount' },
  { value: 'poNumber', label: 'PO Number' },
  { value: 'status', label: 'Status' },
];

// Get status variant for StatusBadge - matches backend status enum
const getStatusVariant = (
  status: PurchaseOrderStatus
): 'success' | 'warning' | 'error' | 'info' | 'purple' | 'neutral' => {
  switch (status) {
    case 'approved':
    case 'complete':
      return 'success';
    case 'pending':
    case 'partial':
      return 'warning';
    case 'cancelled':
    case 'void':
      return 'error';
    case 'sent':
      return 'info';
    case 'draft':
    default:
      return 'neutral';
  }
};

// Get priority variant for PriorityBadge (maps 'urgent' to 'critical')
const getPriorityVariant = (
  priority: PurchaseOrderPriority
): 'low' | 'medium' | 'high' | 'critical' => {
  if (priority === 'urgent') return 'critical';
  return priority;
};

// Format currency
const formatCurrency = (amount: number, currency = 'USD'): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
};

// Format date
const formatDate = (dateString?: string): string => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const PurchaseOrders: React.FC = () => {
  const projectId = useCurrentProjectId();
  const toast = useToast();

  // State for filters and pagination
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<PurchaseOrderStatus | ''>('');
  const [type, setType] = useState<PurchaseOrderType | ''>('');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const limit = 20;

  // State for drawer
  const [selectedPO, setSelectedPO] = useState<PurchaseOrder | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState<'create' | 'edit' | 'view'>('create');

  // State for Send Modal
  const [isSendModalOpen, setIsSendModalOpen] = useState(false);
  const [sendPO, setSendPO] = useState<PurchaseOrder | null>(null);

  // State for Change Order Drawer
  const [isChangeOrderDrawerOpen, setIsChangeOrderDrawerOpen] = useState(false);
  const [changeOrderPO, setChangeOrderPO] = useState<PurchaseOrder | null>(null);

  // State for Receipt Drawer
  const [isReceiptDrawerOpen, setIsReceiptDrawerOpen] = useState(false);
  const [receiptPO, setReceiptPO] = useState<PurchaseOrder | null>(null);

  // State for dropdown menu
  const [activeMenuId, setActiveMenuId] = useState<string | null>(null);

  // API calls
  const {
    data: purchaseOrdersResponse,
    isLoading,
    isFetching,
    refetch,
  } = useGetPurchaseOrdersQuery(
    {
      projectId: projectId || '',
      params: {
        page,
        limit,
        search: search || undefined,
        status: status || undefined,
        type: type || undefined,
        sortBy: sortBy as 'createdAt' | 'issueDate' | 'dueDate' | 'total' | 'poNumber' | 'status',
        sortOrder,
      },
    },
    { skip: !projectId }
  );

  const { data: statsResponse } = useGetPurchaseOrderStatsQuery(
    { projectId: projectId || '' },
    { skip: !projectId }
  );

  const [deletePurchaseOrder, { isLoading: isDeleting }] = useDeletePurchaseOrderMutation();
  const [updatePurchaseOrder] = useUpdatePurchaseOrderMutation();
  const [processApproval] = useProcessPurchaseOrderApprovalMutation();

  // Extract data
  const purchaseOrders = useMemo(
    () => purchaseOrdersResponse?.data?.purchaseOrders || [],
    [purchaseOrdersResponse]
  );
  const pagination = purchaseOrdersResponse?.data?.pagination;
  const stats = statsResponse?.data;

  // Computed stats for header
  const headerStats = useMemo(() => {
    if (!stats) return [];
    return [
      {
        title: 'Total',
        value: stats.overview?.totalPurchaseOrders || 0,
        variant: 'brand' as const,
      },
      {
        title: 'Pending',
        value: stats.byStatus?.pending?.count || 0,
        variant: 'warning' as const,
      },
      {
        title: 'Approved',
        value: stats.byStatus?.approved?.count || 0,
        variant: 'success' as const,
      },
      {
        title: 'Overdue',
        value: stats.overdue?.count || 0,
        variant: 'danger' as const,
      },
    ];
  }, [stats]);

  // Handlers
  const handleCreatePO = useCallback(() => {
    setSelectedPO(null);
    setDrawerMode('create');
    setIsDrawerOpen(true);
  }, []);

  const handleViewPO = useCallback((po: PurchaseOrder) => {
    setSelectedPO(po);
    setDrawerMode('view');
    setIsDrawerOpen(true);
  }, []);

  const handleEditPO = useCallback((po: PurchaseOrder) => {
    setSelectedPO(po);
    setDrawerMode('edit');
    setIsDrawerOpen(true);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setIsDrawerOpen(false);
    setSelectedPO(null);
  }, []);

  const handleDeletePO = useCallback(
    async (po: PurchaseOrder) => {
      if (!projectId) return;
      if (window.confirm(`Are you sure you want to delete PO ${po.poNumber}?`)) {
        try {
          await deletePurchaseOrder({
            projectId,
            purchaseOrderId: po._id,
          }).unwrap();
        } catch (error) {
          console.error('Failed to delete purchase order:', error);
        }
      }
    },
    [projectId, deletePurchaseOrder]
  );

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  // Handle PDF Download
  const handleDownloadPDF = useCallback(
    async (po: PurchaseOrder) => {
      if (!projectId) return;
      try {
        const response = await fetch(
          `/api/projects/${projectId}/purchase-orders/${po._id}/pdf`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
            },
          }
        );
        if (!response.ok) throw new Error('Failed to download PDF');
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${po.poNumber}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        toast.success('PDF downloaded successfully');
      } catch {
        toast.error('Failed to download PDF');
      }
    },
    [projectId, toast]
  );

  // Handle Send to Vendor
  const handleOpenSendModal = useCallback((po: PurchaseOrder) => {
    setSendPO(po);
    setIsSendModalOpen(true);
  }, []);

  const handleCloseSendModal = useCallback(() => {
    setIsSendModalOpen(false);
    setSendPO(null);
  }, []);

  // Handle Change Orders
  const handleOpenChangeOrders = useCallback((po: PurchaseOrder) => {
    setChangeOrderPO(po);
    setIsChangeOrderDrawerOpen(true);
  }, []);

  const handleCloseChangeOrderDrawer = useCallback(() => {
    setIsChangeOrderDrawerOpen(false);
    setChangeOrderPO(null);
  }, []);

  // Handle Receipts
  const handleOpenReceipts = useCallback((po: PurchaseOrder) => {
    setReceiptPO(po);
    setIsReceiptDrawerOpen(true);
  }, []);

  const handleCloseReceiptDrawer = useCallback(() => {
    setIsReceiptDrawerOpen(false);
    setReceiptPO(null);
  }, []);

  // Handle Status Changes
  const handleStatusChange = useCallback(
    async (po: PurchaseOrder, newStatus: PurchaseOrderStatus) => {
      if (!projectId) return;
      try {
        if (newStatus === 'approved') {
          await processApproval({
            projectId,
            purchaseOrderId: po._id,
            data: { approved: true },
          }).unwrap();
          toast.success('Purchase order approved');
        } else {
          await updatePurchaseOrder({
            projectId,
            purchaseOrderId: po._id,
            data: { status: newStatus },
          }).unwrap();
          toast.success(`Status changed to ${newStatus}`);
        }
        refetch();
      } catch {
        toast.error('Failed to update status');
      }
    },
    [projectId, processApproval, updatePurchaseOrder, toast, refetch]
  );

  // Render filters
  const renderFilters = () => (
    <div className="flex items-center gap-2">
      <SearchableDropdown
        placeholder="Status"
        value={status}
        onChange={value => {
          setStatus(value as PurchaseOrderStatus | '');
          setPage(1);
        }}
        options={STATUS_OPTIONS}
        searchable={false}
      />
      <SearchableDropdown
        placeholder="Type"
        value={type}
        onChange={value => {
          setType(value as PurchaseOrderType | '');
          setPage(1);
        }}
        options={TYPE_OPTIONS}
        searchable={false}
      />
    </div>
  );

  // Render pagination
  const renderPagination = () => {
    if (!pagination || pagination.totalPages <= 1) return null;

    const { page: currentPage, totalPages, total } = pagination;
    const startItem = (currentPage - 1) * limit + 1;
    const endItem = Math.min(currentPage * limit, total);

    return (
      <div className="flex items-center justify-between">
        <div className="text-xs text-gray-500">
          Showing {startItem}-{endItem} of {total} results
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="text-xs text-gray-600">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  };

  // Render row actions dropdown using portal-based ActionMenu
  const renderRowActions = (po: PurchaseOrder) => {
    const isMenuOpen = activeMenuId === po._id;

    const menuItems: (ActionMenuItem | 'divider')[] = [
      // View & Edit
      {
        label: 'View Details',
        icon: <Eye className="h-3.5 w-3.5" />,
        onClick: () => handleViewPO(po),
      },
      {
        label: 'Edit',
        icon: <Pencil className="h-3.5 w-3.5" />,
        onClick: () => handleEditPO(po),
        hidden: po.status !== 'draft',
      },
      'divider',
      // Status Actions
      {
        label: 'Submit for Approval',
        icon: <FileText className="h-3.5 w-3.5" />,
        onClick: () => handleStatusChange(po, 'pending'),
        hidden: po.status !== 'draft',
      },
      {
        label: 'Approve',
        icon: <Check className="h-3.5 w-3.5" />,
        onClick: () => handleStatusChange(po, 'approved'),
        variant: 'success',
        hidden: po.status !== 'pending',
      },
      {
        label: 'Reject (Return to Draft)',
        icon: <XCircle className="h-3.5 w-3.5" />,
        onClick: () => handleStatusChange(po, 'draft'),
        variant: 'warning',
        hidden: po.status !== 'pending',
      },
      {
        label: 'Send to Vendor',
        icon: <Send className="h-3.5 w-3.5" />,
        onClick: () => handleOpenSendModal(po),
        hidden: po.status !== 'approved' && po.status !== 'sent',
      },
      'divider',
      // Receipts & Change Orders
      {
        label: 'Record Receipt',
        icon: <ClipboardList className="h-3.5 w-3.5" />,
        onClick: () => handleOpenReceipts(po),
        hidden: !['approved', 'sent', 'partial'].includes(po.status),
      },
      {
        label: 'Change Orders',
        icon: <FileText className="h-3.5 w-3.5" />,
        onClick: () => handleOpenChangeOrders(po),
        hidden: !['approved', 'sent', 'partial'].includes(po.status),
      },
      'divider',
      // PDF Export
      {
        label: 'Download PDF',
        icon: <Download className="h-3.5 w-3.5" />,
        onClick: () => handleDownloadPDF(po),
      },
      'divider',
      // Delete
      {
        label: 'Delete',
        icon: <Trash2 className="h-3.5 w-3.5" />,
        onClick: () => handleDeletePO(po),
        variant: 'danger',
        disabled: isDeleting,
        hidden: po.status !== 'draft' && po.status !== 'cancelled',
      },
    ];

    return (
      <ActionMenu
        id={po._id}
        items={menuItems}
        isOpen={isMenuOpen}
        onOpenChange={open => setActiveMenuId(open ? po._id : null)}
      />
    );
  };

  // Show loading state
  if (isLoading && purchaseOrders.length === 0) {
    return (
      <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
        <LoadingSpinner size="md" />
      </div>
    );
  }

  // Show project selection required
  if (!projectId) {
    return (
      <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
        <EmptyState
          icon={Inbox}
          title="No Project Selected"
          description="Please select a project to view purchase orders."
        />
      </div>
    );
  }

  return (
    <ModuleLayout
      title="Purchase Orders"
      description="Manage purchase orders and vendor relationships"
      icon={<ShoppingCart className="h-4 w-4 text-brand-600" />}
      primaryAction={{
        label: 'New PO',
        onClick: handleCreatePO,
        icon: <Plus className="h-3 w-3 mr-1" />,
      }}
      stats={headerStats}
      searchValue={search}
      onSearchChange={value => {
        setSearch(value);
        setPage(1);
      }}
      filters={renderFilters()}
      sortOptions={SORT_OPTIONS}
      sortValue={sortBy}
      onSortChange={setSortBy}
      sortOrder={sortOrder}
      onSortOrderChange={setSortOrder}
      onRefresh={refetch}
      isLoading={isFetching}
      pagination={renderPagination()}
    >
      {purchaseOrders.length === 0 ? (
        <EmptyState
          icon={ShoppingCart}
          title="No purchase orders found"
          description={
            search || status || type
              ? "Try adjusting your filters to find what you're looking for."
              : 'Get started by creating your first purchase order.'
          }
          action={
            !search && !status && !type
              ? {
                  label: 'Create Purchase Order',
                  onClick: handleCreatePO,
                }
              : undefined
          }
        />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 text-left">
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  PO Number
                </th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Vendor
                </th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Priority
                </th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Issue Date
                </th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Delivery
                </th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {purchaseOrders.map(po => (
                <tr
                  key={po._id}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => handleViewPO(po)}
                >
                  <td className="px-4 py-3">
                    <div className="flex flex-col">
                      <span className="text-sm font-medium text-gray-900">{po.poNumber}</span>
                      {po.title && (
                        <span className="text-xs text-gray-500 truncate max-w-[200px]">
                          {po.title}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col">
                      <span className="text-sm text-gray-900">{po.vendor.name}</span>
                      {po.vendor.contactName && (
                        <span className="text-xs text-gray-500">{po.vendor.contactName}</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm font-medium text-gray-900">
                      {formatCurrency(po.total, po.currency)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge
                      status={po.status}
                      variant={getStatusVariant(po.status)}
                      size="sm"
                    />
                  </td>
                  <td className="px-4 py-3">
                    {po.priority ? (
                      <PriorityBadge priority={getPriorityVariant(po.priority)} size="sm" />
                    ) : (
                      <span className="text-xs text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-gray-600">{formatDate(po.issueDate)}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`text-sm ${
                        po.isOverdue ? 'text-red-600 font-medium' : 'text-gray-600'
                      }`}
                    >
                      {formatDate(po.dueDate)}
                    </span>
                  </td>
                  <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                    {renderRowActions(po)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Purchase Order Drawer */}
      <PurchaseOrderDrawer
        isOpen={isDrawerOpen}
        onClose={handleCloseDrawer}
        purchaseOrder={selectedPO}
        mode={drawerMode}
        onSuccess={refetch}
      />

      {/* Send Purchase Order Modal */}
      {sendPO && (
        <SendPurchaseOrderModal
          isOpen={isSendModalOpen}
          onClose={handleCloseSendModal}
          purchaseOrder={sendPO}
          projectId={projectId || ''}
        />
      )}

      {/* Change Order Drawer */}
      {changeOrderPO && (
        <ChangeOrderDrawer
          isOpen={isChangeOrderDrawerOpen}
          onClose={handleCloseChangeOrderDrawer}
          purchaseOrder={changeOrderPO}
          mode="create"
          onSuccess={refetch}
        />
      )}

      {/* Receipt Drawer */}
      {receiptPO && (
        <ReceiptDrawer
          isOpen={isReceiptDrawerOpen}
          onClose={handleCloseReceiptDrawer}
          purchaseOrder={receiptPO}
          mode="create"
          onSuccess={refetch}
        />
      )}
    </ModuleLayout>
  );
};

export default PurchaseOrders;
